import { useCallback, useEffect } from 'react';

type KeyboardModifier = 'ctrl' | 'cmd' | 'alt' | 'shift';

interface UseKeyboardShortcutOptions {
  key: string;
  modifiers?: KeyboardModifier[];
  enabled?: boolean;
  preventDefault?: boolean;
  stopPropagation?: boolean;
}

export function useKeyboardShortcut(
  callback: () => void,
  options: UseKeyboardShortcutOptions
) {
  const {
    key,
    modifiers = [],
    enabled = true,
    preventDefault = true,
    stopPropagation = false,
  } = options;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Check if the key matches
      if (event.key.toLowerCase() !== key.toLowerCase()) return;

      // Check modifiers
      const hasCtrl = modifiers.includes('ctrl')
        ? event.ctrlKey
        : !event.ctrlKey;
      const hasCmd = modifiers.includes('cmd') ? event.metaKey : !event.metaKey;
      const hasAlt = modifiers.includes('alt') ? event.altKey : !event.altKey;
      const hasShift = modifiers.includes('shift')
        ? event.shiftKey
        : !event.shiftKey;

      // On Mac, Cmd is used instead of Ctrl for most shortcuts
      const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);

      // If using cmd/ctrl modifier, check platform-specific key
      if (modifiers.includes('cmd') || modifiers.includes('ctrl')) {
        const correctModifier = isMac ? event.metaKey : event.ctrlKey;
        if (!correctModifier) return;
      } else if (hasCtrl || hasCmd || hasAlt || hasShift) {
        // If not all required modifiers are pressed, return
        return;
      }

      // Check if we're in an input field (unless it's a global shortcut)
      const target = event.target as HTMLElement;
      const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
      if (isInput && !modifiers.length) return;

      // Prevent default and stop propagation if requested
      if (preventDefault) event.preventDefault();
      if (stopPropagation) event.stopPropagation();

      // Execute callback
      callback();
    },
    [key, modifiers, callback, preventDefault, stopPropagation]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);
}

// Helper hook for common shortcuts
export function useCommonShortcuts() {
  const isMac =
    typeof window !== 'undefined' &&
    /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  return {
    modifierKey: isMac ? 'cmd' : ('ctrl' as KeyboardModifier),
    modifierSymbol: isMac ? '⌘' : 'Ctrl',
  };
}
